import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { getMetaForEspelho } from './calcComissaoEspelhoExcel';
import { contaParaMetaN } from './regrasContagem';
import type { MetaMensal } from './useCommissionRules';
import type { MetaAgregadaGestor } from './useCalculoComissao';

// ============================================================================
// Meta-alvo do GESTOR (supervisor/gerente) = SOMA, por consultor, da meta da
// equipe dele. Como cada consultor entra com a meta da sua equipe, a soma já é
// "meta da equipe × nº de consultores da equipe" — o multiplicador, mas por
// equipe. Respeita individual → equipe → geral (mesma getMetaForEspelho do
// resumo). NÃO mexe em faixa/%, nem na regra de experiência.
//
// Retorna null quando não dá pra agregar (consultor comum, sem dados, ou nenhuma
// meta ativa) → o cálculo cai no comportamento histórico (cargo × multiplicador).
// ============================================================================

type ConsultorHierarquia = {
  consultor_id: string;
  equipe_nome: string | null;
  is_equipe_qualidade?: boolean;
};

export function useMetaAgregadaGestor(
  consultores: ConsultorHierarquia[] | undefined,
  competencia: Date,
  enabled: boolean,
): MetaAgregadaGestor | null {
  const mes = format(startOfMonth(competencia), 'yyyy-MM-dd');

  const { data: base } = useQuery({
    queryKey: ['meta-agregada-base', mes],
    enabled: enabled && !!consultores && consultores.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const [metasRes, equipesRes] = await Promise.all([
        supabase.from('metas_mensais').select('*').eq('competencia', mes).eq('ativo', true),
        supabase.from('equipes').select('id, nome, tipo_pessoa'),
      ]);
      return {
        metas: (metasRes.data ?? []) as MetaMensal[],
        equipes: (equipesRes.data ?? []) as Array<{ id: string; nome: string; tipo_pessoa: string | null }>,
      };
    },
  });

  return useMemo<MetaAgregadaGestor | null>(() => {
    if (!enabled || !consultores || !base) return null;

    const metasGen = base.metas.filter((m) => !m.equipe_id && !m.usuario_id); // por cargo
    const metasEquipe = base.metas.filter((m) => !!m.equipe_id);
    const metasIndiv = base.metas.filter((m) => !!m.usuario_id);
    const nomeToId = new Map(base.equipes.map((e) => [e.nome, e.id]));
    // CPF e CNPJ nunca somam: o gestor soma só as equipes do segmento da hierarquia dele
    // (hierarquia só de equipes CPF → CPF; qualquer outra → CNPJ, como sempre foi).
    const segmentoEquipe = new Map(base.equipes.map((e) => [e.nome, e.tipo_pessoa === 'PF' ? 'PF' : 'PJ'] as const));
    const segConsultor = (c: ConsultorHierarquia) => (c.equipe_nome ? segmentoEquipe.get(c.equipe_nome) ?? 'PJ' : 'PJ');
    const segmento: 'PF' | 'PJ' = consultores.length > 0 && consultores.every((c) => segConsultor(c) === 'PF') ? 'PF' : 'PJ';

    const agg: MetaAgregadaGestor = {
      meta_altas: 0, meta_fibras: 0, meta_fat_vn: 0, meta_fat_total: 0,
      meta_qtd_tt: 0, meta_qtd_mp: 0, meta_qtd_migracao: 0,
      meta_fat_ti: 0, meta_fat_avancada: 0, meta_tv: 0,
      usar_meta_altas: false, usar_meta_fibras: false, usar_meta_fat_vn: false,
      usar_meta_fat_total: false, usar_meta_qtd_tt: false, usar_meta_qtd_mp: false,
      usar_meta_qtd_migracao: false,
      usar_meta_fat_ti: false, usar_meta_fat_avancada: false, usar_meta_tv: false,
    };

    // Soma sobre os consultores que CONTAM para meta (mesma regra do nVendedores).
    for (const c of consultores.filter(contaParaMetaN as any).filter((c) => segConsultor(c) === segmento)) {
      const equipeId = c.equipe_nome ? nomeToId.get(c.equipe_nome) ?? null : null;
      const meta = getMetaForEspelho(metasGen, metasIndiv, 'CONSULTOR', c.consultor_id, metasEquipe, equipeId, segmento);
      if (!meta) continue;
      if (meta.usar_meta_altas)    { agg.meta_altas    += meta.meta_altas    || 0; agg.usar_meta_altas    = true; }
      if (meta.usar_meta_fibras)   { agg.meta_fibras   += meta.meta_fibras   || 0; agg.usar_meta_fibras   = true; }
      if (meta.usar_meta_fat_vn)   { agg.meta_fat_vn   += meta.meta_fat_vn   || 0; agg.usar_meta_fat_vn   = true; }
      if (meta.usar_meta_fat_total){ agg.meta_fat_total+= meta.meta_fat_total|| 0; agg.usar_meta_fat_total= true; }
      if (meta.usar_meta_qtd_tt)   { agg.meta_qtd_tt   += meta.meta_qtd_tt   || 0; agg.usar_meta_qtd_tt   = true; }
      if (meta.usar_meta_qtd_mp)   { agg.meta_qtd_mp   += meta.meta_qtd_mp   || 0; agg.usar_meta_qtd_mp   = true; }
      if (meta.usar_meta_qtd_migracao) { agg.meta_qtd_migracao += meta.meta_qtd_migracao || 0; agg.usar_meta_qtd_migracao = true; }
      // (Douglas 28/07) T.I. e Fixa Avançada agregam igual às demais: o alvo do
      // gestor é a soma do alvo dos consultores dele.
      if (meta.usar_meta_fat_ti)       { agg.meta_fat_ti       += Number(meta.meta_fat_ti ?? 0);       agg.usar_meta_fat_ti = true; }
      if (meta.usar_meta_fat_avancada) { agg.meta_fat_avancada += Number(meta.meta_fat_avancada ?? 0); agg.usar_meta_fat_avancada = true; }
      // (Douglas 21/10/2026) TV CPF: o supervisor CPF soma a meta de TV dos consultores.
      if (meta.usar_meta_tv) { agg.meta_tv = (agg.meta_tv ?? 0) + Number(meta.meta_tv ?? 0); agg.usar_meta_tv = true; }
    }

    // Sem nenhuma meta ativa → não força (cai no comportamento histórico).
    const temAlgumaMeta = agg.usar_meta_altas || agg.usar_meta_fibras || agg.usar_meta_fat_vn
      || agg.usar_meta_fat_total || agg.usar_meta_qtd_tt || agg.usar_meta_qtd_mp || agg.usar_meta_qtd_migracao
      || agg.usar_meta_fat_ti || agg.usar_meta_fat_avancada || agg.usar_meta_tv;
    return temAlgumaMeta ? agg : null;
  }, [enabled, consultores, base]);
}
